/**
 * Donor Pipeline Board — Fundraising Suite (epic TRUS-326), Track C frontend.
 *
 * A drag-and-drop fundraising kanban (Prospect → Cultivation → Solicitation →
 * Stewardship). Reuses the existing platform pipeline API (pipelineAPI /
 * opportunitiesAPI → /api/platform/sales/*) and the backend's `fundraising`
 * pipeline (added in migration 612); it does NOT duplicate the sales engine —
 * the board components here are a focused re-implementation of the same dnd-kit
 * pattern used by SalesPipelinePage, relabeled for donor development.
 *
 * A fundraising "opportunity" is an ask/gift: name = donor, amount = ask amount,
 * closeDate = expected date. Moving a donor across stages never touches the org's
 * B2B customer_status (the backend mirror skips fundraising pipelines).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, Heart, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTabbedChrome } from '@/components/weavein';
import DonorPeoplePicker from '@/components/donors/DonorPeoplePicker';
import type { PersonResult } from '@/services/api/donorProfiles';
import {
  createDonorOpportunity,
  getDonorPipeline,
  listDonorOpportunities,
  moveDonorOpportunity,
  type DonorOpportunity as Opportunity,
  type DonorPipeline as Pipeline,
  type DonorStage as PipelineStageConfig,
} from '@/services/api/donorPipeline';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

// --------------------------------------------------------------------------
// Draggable donor card
// --------------------------------------------------------------------------

interface DonorCardProps {
  opportunity: Opportunity;
  onClick?: (o: Opportunity) => void;
}

function DraggableDonorCard({ opportunity, onClick }: DonorCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: opportunity.id,
    data: { stage: opportunity.stage },
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => !isDragging && onClick?.(opportunity)}
      className="bg-white rounded-lg border border-gray-200 p-3 cursor-grab hover:shadow-md transition-shadow active:cursor-grabbing"
    >
      <h5 className="font-medium text-gray-900 text-sm truncate">
        {opportunity.donorName || opportunity.name || 'Unnamed donor'}
      </h5>
      {opportunity.company ? <p className="text-xs text-gray-500 truncate">{opportunity.company}</p> : null}
      {opportunity.donorMemberId ? (
        <p className="text-[11px] text-gray-400 truncate">
          Linked donor
          {opportunity.lifetimeGiving
            ? ` · $${Math.round(opportunity.lifetimeGiving).toLocaleString('en-US')} lifetime`
            : ''}
        </p>
      ) : (
        <p className="text-[11px] text-amber-600 truncate">Not linked to a donor record</p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-base font-bold text-gray-900">{formatCurrency(opportunity.amount)}</span>
        <span className="text-xs text-gray-500">{opportunity.probability}%</span>
      </div>
      {opportunity.closeDate ? (
        <p className="mt-1 text-xs text-gray-400">Expected: {opportunity.closeDate}</p>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------------------
// Droppable stage column
// --------------------------------------------------------------------------

interface StageColumnProps {
  stage: PipelineStageConfig;
  opportunities: Opportunity[];
  onAdd: (stageId: string) => void;
  onCardClick: (o: Opportunity) => void;
}

function StageColumn({ stage, opportunities, onAdd, onCardClick }: StageColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, data: { stage: stage.id } });
  const total = opportunities.reduce((sum, o) => sum + (o.amount || 0), 0);
  return (
    <div className="flex-shrink-0 w-64 sm:w-72">
      <div className="rounded-t-lg p-3 bg-gray-50 border-t-4" style={{ borderTopColor: stage.color }}>
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">{stage.name}</h4>
          <span className="text-sm text-gray-600">{opportunities.length}</span>
        </div>
        <div className="mt-1 flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">{formatCurrency(total)}</p>
          <span className="text-xs text-gray-500">{stage.probability}%</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`bg-gray-50 rounded-b-lg p-2 min-h-[400px] space-y-2 transition-colors ${
          isOver ? 'bg-blue-50 ring-2 ring-blue-300' : ''
        }`}
      >
        <SortableContext items={opportunities.map((o) => o.id)} strategy={verticalListSortingStrategy}>
          {opportunities.map((o) => (
            <DraggableDonorCard key={o.id} opportunity={o} onClick={onCardClick} />
          ))}
        </SortableContext>
        {!stage.isWon && !stage.isLost ? (
          <button
            onClick={() => onAdd(stage.id)}
            className="w-full p-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded border border-dashed border-gray-300"
          >
            + Add donor
          </button>
        ) : null}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Add-donor modal
// --------------------------------------------------------------------------

interface AddDonorFormProps {
  stageId: string;
  stages: PipelineStageConfig[];
  onClose: () => void;
  onCreate: (data: {
    name: string;
    company: string;
    amount: number;
    stage: string;
    closeDate: string;
    donorMemberId?: number;
  }) => void;
  saving: boolean;
}

/**
 * Full-surface "add an ask" form.
 *
 * The important change is the donor PICKER: cards used to be free-typed names
 * with no relationship to any record, so `donor_member_id` was never set — which
 * is why the contact record's cultivation-stage panel (which filters on exactly
 * that field) was permanently empty. Binding is still optional, because a brand
 * new prospect may not have a record yet.
 */
function AddDonorForm({ stageId, stages, onClose, onCreate, saving }: AddDonorFormProps) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [amount, setAmount] = useState('');
  const [stage, setStage] = useState(stageId);
  const [closeDate, setCloseDate] = useState('');
  const [linked, setLinked] = useState<PersonResult | null>(null);
  const [showPicker, setShowPicker] = useState(true);

  const canSave = (name.trim().length > 0 || !!linked) && !saving;
  const field =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
  const label = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Add an ask</h1>
          <p className="text-sm text-gray-500">Track a gift you are cultivating through the pipeline.</p>
        </div>
      </div>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Donor</h2>
        {linked ? (
          <div className="flex items-center justify-between rounded border border-blue-100 bg-blue-50/60 px-3 py-2 dark:border-blue-900 dark:bg-blue-900/20">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{linked.name}</div>
              <div className="truncate text-xs text-gray-500">
                {linked.email || 'No email on record'}
                {linked.lifetime_giving > 0
                  ? ` · $${Math.round(linked.lifetime_giving).toLocaleString('en-US')} lifetime`
                  : ''}
              </div>
            </div>
            <button
              onClick={() => {
                setLinked(null);
                setShowPicker(true);
              }}
              className="text-xs text-gray-500 hover:underline"
            >
              Change
            </button>
          </div>
        ) : showPicker ? (
          <>
            <DonorPeoplePicker
              multiple={false}
              addLabel="Use this donor"
              onAdd={(people) => {
                const p = people[0];
                if (!p) return;
                setLinked(p);
                setName(p.name);
                setCompany((c) => c);
                setShowPicker(false);
              }}
            />
            <button onClick={() => setShowPicker(false)} className="text-xs text-gray-500 hover:underline">
              Or enter a name manually
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <div>
              <label className={label}>Donor name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={field} placeholder="Jane Donor" />
            </div>
            <button onClick={() => setShowPicker(true)} className="text-xs text-blue-600 hover:underline">
              Search your records instead
            </button>
          </div>
        )}
        <div>
          <label className={label}>Organization (optional)</label>
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={field}
            placeholder="Foundation / employer"
          />
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">The ask</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label}>Ask / gift amount</label>
            <input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={field}
              placeholder="0"
            />
          </div>
          <div>
            <label className={label}>Expected date</label>
            <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} className={field} />
          </div>
        </div>
        <div>
          <label className={label}>Stage</label>
          <select value={stage} onChange={(e) => setStage(e.target.value)} className={field}>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          onClick={() =>
            onCreate({
              name: (name || linked?.name || '').trim(),
              company: company.trim(),
              amount: parseFloat(amount) || 0,
              stage,
              closeDate,
              donorMemberId: linked?.id,
            })
          }
        >
          {saving ? 'Saving…' : 'Add to pipeline'}
        </Button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Main board
// --------------------------------------------------------------------------

export function DonorPipelineBoard() {
  const tabbed = useTabbedChrome();
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [addStage, setAddStage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pl, opps] = await Promise.all([getDonorPipeline(), listDonorOpportunities()]);
      setPipeline(pl);
      setOpportunities(opps);
    } catch (e) {
      setError('Could not load the donor pipeline. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const opportunitiesByStage = useCallback(
    (stageId: string) => opportunities.filter((o) => o.stage === stageId),
    [opportunities],
  );

  const activeStages = useMemo(() => pipeline?.stages.filter((s) => !s.isWon && !s.isLost) ?? [], [pipeline]);
  const closedStages = useMemo(() => pipeline?.stages.filter((s) => s.isWon || s.isLost) ?? [], [pipeline]);
  const activeOpportunity = activeId ? opportunities.find((o) => o.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => setActiveId(event.active.id as string);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const overStage = over.data.current?.stage as string | undefined;
    if (!overStage) return;
    const opp = opportunities.find((o) => o.id === active.id);
    if (!opp || opp.stage === overStage) return;

    const stageCfg = pipeline?.stages.find((s) => s.id === overStage);
    const probability = stageCfg?.probability ?? opp.probability;
    // Optimistic update, then persist.
    setOpportunities((prev) =>
      prev.map((o) => (o.id === opp.id ? { ...o, stage: overStage, probability } : o)),
    );
    moveDonorOpportunity(opp.id, overStage, probability).catch(() => load());
  };

  const handleCreate = async (data: {
    name: string;
    company: string;
    amount: number;
    stage: string;
    closeDate: string;
    donorMemberId?: number;
  }) => {
    if (!pipeline) return;
    setSaving(true);
    try {
      const created = await createDonorOpportunity({
        name: data.name,
        company: data.company,
        amount: data.amount,
        stage: data.stage,
        closeDate: data.closeDate,
        owner: '',
        donorMemberId: data.donorMemberId,
      });
      setOpportunities((prev) => [created, ...prev]);
      setAddStage(null);
    } catch (e) {
      setError('Could not add the donor. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // The add form is its own surface — the board is not rendered behind it.
  if (addStage && pipeline) {
    return (
      <AddDonorForm
        stageId={addStage}
        stages={pipeline.stages}
        onClose={() => setAddStage(null)}
        onCreate={handleCreate}
        saving={saving}
      />
    );
  }

  const totalPipeline = opportunities
    .filter((o) => {
      const s = pipeline?.stages.find((st) => st.id === o.stage);
      return !s?.isLost;
    })
    .reduce((sum, o) => sum + (o.amount || 0), 0);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!tabbed && <Heart size={26} className="text-blue-600" />}
          <div>
            {!tabbed && <h1 className="text-xl font-semibold text-gray-900">Donor pipeline</h1>}
            <p className="text-sm text-gray-500">
              Move donors through cultivation → stewardship. {formatCurrency(totalPipeline)} in open pipeline.
            </p>
          </div>
        </div>
        <button
          onClick={() => load()}
          className="flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </header>

      {error ? (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading donor pipeline…</div>
      ) : !pipeline ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
          <Heart size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600">No donor pipeline yet.</p>
          <p className="mt-1 text-sm text-gray-400">
            It is created automatically on first load. Try Refresh, or contact support if it doesn’t appear.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {activeStages.map((stage) => (
              <StageColumn
                key={stage.id}
                stage={stage}
                opportunities={opportunitiesByStage(stage.id)}
                onAdd={(s) => setAddStage(s)}
                onCardClick={() => undefined}
              />
            ))}
            {closedStages.map((stage) => (
              <StageColumn
                key={stage.id}
                stage={stage}
                opportunities={opportunitiesByStage(stage.id)}
                onAdd={(s) => setAddStage(s)}
                onCardClick={() => undefined}
              />
            ))}
          </div>

          <DragOverlay>
            {activeOpportunity ? (
              <div className="w-72 rounded-lg border-2 border-blue-400 bg-white p-3 shadow-lg">
                <h5 className="text-sm font-medium text-gray-900">{activeOpportunity.name}</h5>
                {activeOpportunity.company ? <p className="text-sm text-gray-500">{activeOpportunity.company}</p> : null}
                <p className="text-lg font-bold text-gray-900">{formatCurrency(activeOpportunity.amount)}</p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

    </div>
  );
}

export default DonorPipelineBoard;
